<script>
import ModernInput from '@/components/ModernInput.vue';
import PlansPartnerForm from '@/views/Manage/Plans/PlansPartnerForm.vue';
import {
  read, remove,
} from '@/api/plan_partners';
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import ReportsTableTooltip from '@/components/Widgets/ReportsTableTooltip.vue';
import ModernSelect from '@/components/ModernSelect.vue';
import ModernTable from '@/components/ModernTable.vue';

export default {
  name: 'PlansPartner',
  components: {
    ModernTable, ModernSelect, ReportsTableTooltip, ModernInput,
  },

  data() {
    return {
      isLoading: false,
      columns: [],
      data: [],
      searchText: null,
      pager: {
        total: 0,
        limit: 25,
        page: 1,
        perPageOptions: [{ value: 10 }, { value: 25 }, { value: 50 }, { value: 100 }],
      },
      sorter: {
        sortField: 'id',
        sortOrder: 'desc',
        defaultSort: 'id',
        defaultSortDirection: 'desc',
      },
      isLeftSticky: false,
      isTopSticky: true,
      PLAN_MODIFY: { resource: 'manage-plans', code: 'modify' },
    };
  },

  async mounted() {
    await this.search();
  },

  async activated() {
    await this.search();
  },

  computed: {
    ...mapGetters(['authAuthenticator']),
  },
  methods: {

    async search() {
      let order = null;
      const direction = `${this.sorter.sortOrder === 'desc' ? '-' : ''}`;
      if (this.sorter.sortField === 'discount') {
        order = `${direction}discountUnit,${direction}${this.sorter.sortField}`;
      } else if (this.sorter.sortField === 'commission') {
        order = `${direction}commissionUnit,${direction}${this.sorter.sortField}`;
      } else {
        order = `${this.sorter.sortOrder === 'desc' ? '-' : ''}${this.sorter.sortField}`;
      }

      this.isLoading = true;
      const { data } = await read({
        page: this.pager.page,
        limit: this.pager.limit,
        order,
        search: this.searchText ? this.searchText.trim() : null,
      });
      this.data = data.data;
      this.pager.total = data.total;
      this.pager.page = parseInt(data.page, 10);
      this.isLoading = false;
    },

    sort(field, order) {
      this.sorter.sortField = field;
      this.sorter.sortOrder = order;
      this.search();
    },

    pageChange(page) {
      this.pager.page = page;
      this.search();
    },

    async perPageChange() {
      this.pager.page = 1;
      await this.search();
    },

    async onCreate() {
      await this.openPlanFormModal();
    },

    async onEdit(row) {
      await this.openPlanFormModal(row);
    },

    async onDelete(row) {
      const searchFn = this.search;
      this.$buefy.dialog.confirm({
        title: 'Deleting plan',
        message: 'Are you sure you want to <b>delete</b> this plan? This action cannot be undone.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        ariaModal: true,
        async onConfirm() {
          try {
            await remove(row);
            await searchFn();
          } catch (error) {
            this.$buefy.snackbar.open({
              type: 'is-danger',
              message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
              position: 'is-top',
              indefinite: true,
              actionText: ' ',
            });
          }
        },
      });
    },

    async viewPartner(row) {
      await this.$router.push({ name: 'manage-users', params: { activeTab: 'PARTNERS', planFilter: row } });
    },

    async openPlanFormModal(plan = null) {
      const props = {};
      const searchFn = this.search;

      if (plan) {
        props.plan = plan;
      }

      await this.$buefy.modal.open({
        parent: this,
        component: PlansPartnerForm,
        hasModalCard: true,
        trapFocus: true,
        scroll: 'keep',
        ariaModal: true,
        customClass: 'modern-fix-modal',
        canCancel: ['outside'],
        props,
        events: {
          async submit() {
            await searchFn();
          },
        },
      });
    },

    numberFormat(value, unit) {
      const usFormatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        style: 'currency',
        currency: 'USD',
      });

      const percentFormatter = new Intl.NumberFormat('en-US');

      if (unit === '%') {
        return percentFormatter.format(value) + unit;
      }

      if (unit === '$') {
        return usFormatter.format(value, 2);
      }
      return '';
    },
  },
};
</script>

<template>
  <div class="grid-module" ref="module">
    <div class="columns">
      <div class="column is-2 btn-flex sm-show" style="display: none; min-width: 180px; gap: .75em">
        <b-button
          v-if="authAuthenticator(PLAN_MODIFY)"
          class="button-right modern-button"
          type="outlined"
          position="is-right"
          style="max-width: 160px; width: 160px; margin-top: .75em; flex-grow: 1"
          @click="onCreate">
          NEW PLAN
        </b-button>
      </div>
      <div class="column">
        <form id="usersFilter">
          <section class="modern-form-group">
            <div class="modern-input-controlled">
              <ModernInput  placeholder="Search..."
                            name="searchText"
                            v-model.trim="searchText"
                            type="search"/>
              <div class="modern-input-control">
                <button class="modern-button" @click.prevent="search">
                  <i class="mdi mdi-magnify"></i>
                </button>
              </div>
            </div>
          </section>
        </form>
      </div>
      <div class="column is-2 btn-flex sm-hide"
           style="align-content: end; min-width: 180px; gap: .75em">
        <b-button
          v-if="authAuthenticator(PLAN_MODIFY)"
          class="button-right modern-button"
          type="outlined"
          position="is-right"
          style="max-width: 160px; width: 160px; margin-top: .75em; flex-grow: 1"
          @click="onCreate">
          NEW PLAN
        </b-button>
      </div>
    </div>
    <section class="data-grid" style="padding-bottom: 2em">
      <template v-if="!data.length">
        <b-table
          class="modern-table"
          height="100%"
          :columns="[
            { name: '', label: '', centered: true,
              cellClass: 'cell-costil',
              headerClass: 'costil',
            },
            { name: 'id', label: 'ID', centered: true },
            { name: 'name', label: 'Name' },
            { name: 'discount', label: 'Discount' },
            { name: 'commission', label: 'Commission' },
            { name: 'isSpecial', label: 'Special' },
            { name: '', label: '' },
          ]"
          :loading="isLoading"
        >
          <template #header="{ column }">
            <div :class="[{'top-sticked': isTopSticky}, column['thclass'],
         'modern-table-header-label']">
              {{ column['label'] }}
              <template v-if="column['meta']">
                <reports-table-tooltip :content="column['meta']" />
              </template>
            </div>
          </template>

          <template #empty v-if="!isLoading">
            <div style="padding-top: 1em; overflow: hidden; text-align: center">
          <span class="icon">
            <i class="fal fa-frown fa-3x"></i>
          </span>
              <p>Nothing here</p>
            </div>
          </template>
        </b-table>
      </template>
      <template v-else>
        <ModernTable
          ref="table"
          class="modern-table"
          height="100%"
          :loading="isLoading"
          :per-page="pager.limit"
          :total="pager.total"
          :current-page.sync="pager.page"
          :data="data"
          paginated
          hoverable
          pagination-size="is-medium"
          backend-pagination
          backend-sorting
          :defaultSort="sorter.defaultSort"
          :default-sort-direction="sorter.defaultSortDirection"
          @sort="sort"
          @page-change="pageChange"
        >

          <template #header="{ column }">
            <div :class="[{'top-sticked': isTopSticky, 'm-centered': column['centered'] },
             column['thclass'], 'modern-table-header-label']"
                 :ref="column['field']">
              {{ column['label'] }}
              <template v-if="column['meta']">
                <reports-table-tooltip :content="column['meta']" />
              </template>
            </div>
          </template>

          <template #default="{ row }">
            <b-table-column field="ids" label="" width="0" cell-class="cell-costil"
                            header-class="costil" sticky>
            </b-table-column>
            <b-table-column field="id" label="ID" width="100" sortable>
              {{ row['id'] }}
            </b-table-column>
            <b-table-column field="name" width="550" label="Name" sortable>
              {{ row['name'] }}
            </b-table-column>
            <b-table-column field="discount" label="Discount"
                            width="150"
                            style="text-align: right" sortable>
              {{ numberFormat(row['discount'], row['discountUnit']) }}
            </b-table-column>
            <b-table-column field="commission"
                            width="180"
                            label="Commissions" style="text-align: right"
                            sortable>
              {{ numberFormat(row['commission'], row['commissionUnit']) }}
            </b-table-column>
            <b-table-column width="150" field="isSpecial" label="Special" sortable>
              {{ row['isSpecial'] ? 'Yes' : 'No' }}
            </b-table-column>
            <b-table-column width="144" class="action-column" v-if="authAuthenticator(PLAN_MODIFY)">
              <div>
                <span class="icon is-success action-button" title="Edit plan"
                      @click="onEdit(row)">
                <i class="fas fa-pencil-alt fa-lg"></i>
                </span>
                <span class="icon is-success action-button" title="Delete plan"
                      @click="onDelete(row)">
                  <i class="fas fa-trash-alt fa-lg"></i>
                </span>
              </div>
            </b-table-column>
          </template>

          <template #bottom-left>

            <div class="modern-input-labeled">
              <label class="per-page" for="paymentStatus"
                     style="font-weight: 700; white-space: nowrap">
                Per page
              </label>
              <ModernSelect
                style="margin-right: 1em"
                v-model="pager.limit"
                :options="pager.perPageOptions"
                @input="perPageChange"
              />
            </div>
          </template>

          <template #empty v-if="!isLoading">
            <div style="padding-top: 1em; overflow: hidden; text-align: center">
              <span class="icon">
                <i class="fal fa-frown fa-3x"></i>
              </span>
              <p>Nothing here</p>
            </div>
          </template>
        </ModernTable>
      </template>
    </section>
  </div>
</template>

<style  lang="stylus">
@require "../../../assets/style/modern/variables.styl";
</style>

<style scoped lang="stylus">
@require "../../../assets/style/modern/variables.styl";
::v-deep .m-centered
  text-align center
  margin-right unset

::v-deep .cell-costil
  padding 0 !important
::v-deep .costil
  width 0
  padding 0

td
  overflow hidden
  text-overflow ellipsis

@media (max-width: $breakpoints.md)
  .modern-input-controlled
    padding-top 0
  .modern-input-labeled, .modern-input-controlled
    margin-right 0
  .sm-hide
    display none !important
  .sm-show
    display flex !important


.btn-flex
  display: flex;
  justify-content: end
  @media screen and (max-width: $breakpoints.md)
    width 100%
    justify-content: end
    .modern-button
      max-width 160px;

.action-column
  text-align right
.action-column .icon
  color $colors.is-success
  display inline-block
  margin 5px 1em
  &:hover
    color $colors.is-success-alt
.action-column .icon a
  color $colors.is-success
.action-column .icon a:hover
    color $colors.is-success-alt
.action-button
  cursor pointer
  display inline-block
  margin 0 .5rem
</style>
